import { useCallback, useMemo } from 'react'
import { useAppStore } from '@/shared/stores/appStore'
import { en } from './en'
import { ptBR } from './pt-BR'
import { translate } from './translate'
import type { Dictionary, Language, TranslationKey, TranslationVars } from './types'

const DICTIONARIES: Record<Language, Dictionary> = {
  'pt-BR': ptBR,
  en,
}

export type TranslateFn = (key: TranslationKey, vars?: TranslationVars) => string

/**
 * `const { t, language } = useTranslation()`.
 *
 * `t` is stable per language, so it is safe in a `useMemo`/`useCallback`
 * dependency list without re-running on every render.
 */
export function useTranslation(): { t: TranslateFn; language: Language } {
  const language = useAppStore((s) => s.language)
  const dictionary = useMemo(() => DICTIONARIES[language], [language])

  const t = useCallback<TranslateFn>(
    (key, vars) => translate(dictionary, language, key, vars),
    [dictionary, language],
  )

  return { t, language }
}

/**
 * Same lookup outside React — query functions, stores, route loaders. Reads the
 * language at call time, so it always reflects the current choice.
 */
export function translateNow(key: TranslationKey, vars?: TranslationVars): string {
  const language = useAppStore.getState().language
  return translate(DICTIONARIES[language], language, key, vars)
}
